import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { ApiError, api } from "../../lib/api";
import { authClient } from "../../lib/auth";
import { ROLE_LABEL } from "../../lib/format";
import type { Role } from "../../lib/types";
import { AuthFrame, AuthLayout } from "./AuthShell";

const BRAND = {
  side: "left" as const,
  title: "Te damos la bienvenida",
  text: "En Orbis tu equipo atiende el WhatsApp de la empresa, con una IA que solo habla de lo suyo.",
};

interface InvitationInfo {
  tenantName: string;
  role: Role;
  email: string;
  state: "pending" | "accepted" | "revoked" | "expired";
}

const STATE_MESSAGE: Record<Exclude<InvitationInfo["state"], "pending">, string> = {
  accepted: "Esta invitación ya se usó.",
  revoked: "Esta invitación fue cancelada. Pide una nueva a quien te invitó.",
  expired: "Esta invitación venció. Pide una nueva a quien te invitó.",
};

/** Landing page of an invitation link: who invites, for which role, and how to join. */
export function InvitationPage() {
  const { token } = useParams({ from: "/invitacion/$token" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = authClient.useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.get<InvitationInfo>(`/invitations/${token}`),
    retry: false,
  });
  const next = `/invitacion/${token}`;

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/invitations/${token}/accept`);
      qc.clear();
      navigate({ to: "/bandeja" });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "forbidden"
          ? `Esta invitación es para ${invitation.data?.email}. Cierra sesión y entra con ese correo.`
          : "No pudimos aceptar la invitación. Intenta de nuevo.",
      );
    } finally {
      setBusy(false);
    }
  }

  const info = invitation.data;
  let content: ReactNode;
  if (invitation.isPending) {
    content = <AuthLayout title="Cargando invitación…" subtitle="Un momento." />;
  } else if (invitation.isError || !info) {
    content = (
      <AuthLayout title="Invitación no encontrada" subtitle="Revisa que el enlace esté completo o pide uno nuevo.">
        <Link className="auth-secondary" to="/login">
          Ir a iniciar sesión
        </Link>
      </AuthLayout>
    );
  } else if (info.state !== "pending") {
    content = (
      <AuthLayout title="Invitación no disponible" subtitle={STATE_MESSAGE[info.state]}>
        <Link className="auth-secondary" to="/login">
          Ir a iniciar sesión
        </Link>
      </AuthLayout>
    );
  } else {
    content = (
      <AuthLayout
        title={`Te invitaron a ${info.tenantName}`}
        subtitle={`Vas a entrar como ${ROLE_LABEL[info.role]}. La invitación es para ${info.email}.`}
      >
        {session.data ? (
          <>
            <p className="auth-alt">Sesión iniciada como {session.data.user.email}.</p>
            {error && (
              <p className="error-text" role="alert">
                {error}
              </p>
            )}
            <button className="auth-submit" type="button" onClick={accept} disabled={busy} aria-busy={busy}>
              {busy && <span className="auth-spinner" aria-hidden="true" />}
              {busy ? "Uniéndote…" : `Unirme a ${info.tenantName}`}
            </button>
            <button
              className="auth-link"
              type="button"
              onClick={async () => {
                await authClient.signOut();
                session.refetch();
              }}
            >
              Usar otra cuenta
            </button>
          </>
        ) : (
          <>
            <Link className="auth-submit" to="/registro" search={{ next }}>
              Crear mi cuenta
            </Link>
            <Link className="auth-secondary" to="/login" search={{ next }}>
              Ya tengo cuenta
            </Link>
          </>
        )}
      </AuthLayout>
    );
  }

  return <AuthFrame brand={BRAND}>{content}</AuthFrame>;
}
